
import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { googleReviews } from '@/data/googleReviews';

type Testimonial = {
  quote: string;
  author: string;
  role?: string;
  source?: string;
};

type TestimonialsProps = {
  testimonials: Testimonial[];
  title?: string;
  subtitle?: string;
  autoplay?: boolean;
  interval?: number;
  useGoogleReviews?: boolean;
};

const Testimonials = ({ 
  testimonials, 
  title = "What our guests say",
  subtitle,
  autoplay = true,
  interval = 8000,
  useGoogleReviews = false
}: TestimonialsProps) => {
  // Use Google reviews if specified, otherwise use the original testimonials or fallback to persona-based ones
  const testimonialsToShow = useGoogleReviews ? googleReviews : (testimonials.length > 0 ? testimonials : [
    {
      quote: "As a New York native, I thought I knew this city. This tour opened my eyes to a whole new layer of history I'd never known about. Seeing my own neighborhood through the lens of its feline past was fascinating!",
      author: "Sarah K.",
      role: "Brooklyn local"
    },
    {
      quote: "The perfect activity for our weekend visit! Our guide was so knowledgeable and brought these historical cat stories to life. My kids were captivated the entire time - much better than any museum we visited.",
      author: "Michael T.",
      role: "Visiting with family from Chicago"
    },
    {
      quote: "I've done dozens of walking tours across Europe and America, and this was genuinely one of the most unique and memorable. The stories were so well-researched and the connection between cats and New York's development was something I'd never considered before.",
      author: "Emma L.",
      role: "Experienced traveler"
    }
  ]);
  
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (!autoplay) return;
    
    const intervalId = setInterval(() => {
      nextTestimonial();
    }, interval);

    return () => clearInterval(intervalId);
  }, [testimonialsToShow.length, currentIndex, autoplay, interval]);

  const nextTestimonial = () => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev + 1) % testimonialsToShow.length);
    
    setTimeout(() => {
      setIsAnimating(false);
    }, 500);
  };

  const prevTestimonial = () => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev - 1 + testimonialsToShow.length) % testimonialsToShow.length);
    
    setTimeout(() => {
      setIsAnimating(false);
    }, 500);
  };

  const handleDotClick = (index: number) => {
    if (isAnimating || index === currentIndex) return;
    
    setIsAnimating(true);
    setCurrentIndex(index);
    
    setTimeout(() => {
      setIsAnimating(false);
    }, 500);
  };

  return (
    <section className="py-20 md:py-28 bg-neutral-paper relative">
      <div className="absolute inset-0 bg-paw-pattern opacity-[0.03]"></div>
      <div className="container mx-auto px-4 max-w-5xl relative z-10">
        <div className="text-center mb-10">
          <span className="text-gold font-cormorant italic text-xl">What People Are Saying</span>
          <h2 className="section-title-centered mt-2">{title}</h2>
          {subtitle && <p className="text-lg max-w-3xl mx-auto">{subtitle}</p>}
        </div>
        
        <div className="premium-card bg-white border-gold/30 relative">
          {/* Navigation arrows */}
          <button 
            onClick={prevTestimonial}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-white/80 hover:bg-white text-primary shadow-elegant hover:shadow-premium transition-all duration-200"
            aria-label="Previous testimonial"
          >
            <ChevronLeft size={24} />
          </button>
          
          <button 
            onClick={nextTestimonial}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-white/80 hover:bg-white text-primary shadow-elegant hover:shadow-premium transition-all duration-200"
            aria-label="Next testimonial"
          >
            <ChevronRight size={24} />
          </button>
          
          <div className="relative h-full overflow-hidden px-12 py-8">
            {testimonialsToShow.map((testimonial, index) => (
              <div
                key={index}
                className={`transition-all duration-500 ${
                  currentIndex === index 
                    ? 'opacity-100 transform-none' 
                    : index < currentIndex 
                      ? 'opacity-0 translate-x-full absolute inset-0' 
                      : 'opacity-0 -translate-x-full absolute inset-0'
                }`}
              >
                <div className="flex flex-col items-center">
                  <div className="text-gold text-6xl font-cormorant mb-6 opacity-90">"</div>
                  <p className="text-lg md:text-xl italic font-crimson mb-8 text-text text-center px-6 leading-relaxed">
                    {testimonial.quote}
                  </p>
                  <div className="w-16 h-[1px] bg-gold/40 my-6"></div>
                  <div className="text-center">
                    <p className="font-cormorant font-bold text-primary text-xl">
                      {testimonial.author}
                    </p>
                    {testimonial.role && (
                      <p className="text-text/70 text-sm font-lora mt-1">{testimonial.role}</p>
                    )}
                    {testimonial.source && (
                      <p className="text-text/50 text-xs font-lora mt-1">{testimonial.source}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-center mt-6 pb-4">
            {testimonialsToShow.map((_, index) => (
              <button
                key={index}
                className={`w-2 h-2 mx-2 rounded-full transition-all ${
                  currentIndex === index ? "bg-gold w-6 scale-y-125" : "bg-primary/20"
                }`}
                onClick={() => handleDotClick(index)}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
